import { Ticket } from "./Ticket";
import { IndexerLotteryStatistics, xLotteryConfig } from "../web3/Types";
import { USDC } from "./UsdcValue";
import { Frequency } from "./Frequency";

export class LotteryStatistics {
  totalAwarded: USDC;
  totalRaised: USDC;
  highestPrizeWon: USDC;
  highestJackpot: USDC;
  jackpotsWon: bigint;
  playersEntered: bigint;
  ticketsBought: bigint;
  winningTickets: bigint;
  winRatio: number;
  numberFrequency: Map<number, number>;
  starFrequency: Map<number, number>;
  numberLastSeen: Map<number, number>;
  starLastSeen: Map<number, number>;
  mostOverdueTicket: Ticket;
  hottestTicket: Ticket;
  mostFrequentTicket: Ticket;
  leastFrequentTicket: Ticket;

  constructor(
    totalAwarded: USDC,
    totalRaised: USDC,
    highestPrizeWon: USDC,
    highestJackpot: USDC,
    jackpotsWon: bigint,
    playersEntered: bigint,
    ticketsBought: bigint,
    winningTickets: bigint,
    numberFrequency: Map<number, number>,
    starFrequency: Map<number, number>,
    numberLastSeen: Map<number, number>,
    starLastSeen: Map<number, number>,
    mostOverdueTicket: Ticket,
    hottestTicket: Ticket,
    mostFrequentTicket: Ticket,
    leastFrequentTicket: Ticket,
  ) {
    this.totalAwarded = totalAwarded;
    this.totalRaised = totalRaised;
    this.highestPrizeWon = highestPrizeWon;
    this.highestJackpot = highestJackpot;
    this.jackpotsWon = jackpotsWon;
    this.playersEntered = playersEntered;
    this.ticketsBought = ticketsBought;
    this.winningTickets = winningTickets;
    this.winRatio =
      ticketsBought > 0n ? Number(winningTickets) / Number(ticketsBought) : 0;
    this.numberFrequency = numberFrequency;
    this.starFrequency = starFrequency;
    this.numberLastSeen = numberLastSeen;
    this.starLastSeen = starLastSeen;
    this.mostOverdueTicket = mostOverdueTicket;
    this.hottestTicket = hottestTicket;
    this.mostFrequentTicket = mostFrequentTicket;
    this.leastFrequentTicket = leastFrequentTicket;
  }

  static empty(): LotteryStatistics {
    return new LotteryStatistics(
      USDC.empty(),
      USDC.empty(),
      USDC.empty(),
      USDC.empty(),
      0n,
      0n,
      0n,
      0n,
      new Map<number, number>(),
      new Map<number, number>(),
      new Map<number, number>(),
      new Map<number, number>(),
      Ticket.empty(),
      Ticket.empty(),
      Ticket.empty(),
      Ticket.empty(),
    );
  }

  static fromGraph(
    _numbers: Frequency[],
    _stars: Frequency[],
    lotteryData: IndexerLotteryStatistics,
    lotteryConfig: xLotteryConfig,
  ): LotteryStatistics {
    const numbers: Frequency[] = [];
    const stars: Frequency[] = [];
    for (
      let i: number = 0;
      i < Number(lotteryConfig.pools?.mainNumbers ?? 0n);
      i++
    ) {
      const freq = _numbers.find((freq) => Number(freq.id) === i + 1);
      if (freq) {
        numbers.push(freq);
      } else {
        numbers.push(new Frequency(BigInt(i + 1), 0n, 0n));
      }
    }
    for (
      let i: number = 0;
      i < Number(lotteryConfig.pools?.luckyStars ?? 0n);
      i++
    ) {
      const freq = _stars.find((freq) => Number(freq.id) === i + 1);
      if (freq) {
        stars.push(freq);
      } else {
        stars.push(new Frequency(BigInt(i + 1), 0n, 0n));
      }
    }
    const stats = LotteryStatistics.parseNumberStatistics(
      Number(lotteryConfig.pools?.mainNumbers ?? 0n),
      Number(lotteryConfig.pools?.luckyStars ?? 0n),
      Number(lotteryConfig.jackpotTier?.mainNumbers ?? 0n),
      Number(lotteryConfig.jackpotTier?.luckyStars ?? 0n),
      numbers,
      stars,
    );
    return new LotteryStatistics(
      new USDC(lotteryData.prizesWon),
      new USDC(lotteryData.proceeds_donations),
      new USDC(lotteryData.highestPrizeWon),
      new USDC(lotteryData.highestJackpot),
      lotteryData.jackpotsWon,
      lotteryData.playersEntered,
      lotteryData.ticketsBought,
      lotteryData.winningTickets,
      stats?.numberFrequencyMap ?? new Map<number, number>(),
      stats?.starFrequencyMap ?? new Map<number, number>(),
      stats?.numberLastSeenMap ?? new Map<number, number>(),
      stats?.starLastSeenMap ?? new Map<number, number>(),
      stats?.overdueTicket ?? Ticket.empty(),
      stats?.hottestTicket ?? Ticket.empty(),
      stats?.mostFrequentTicket ?? Ticket.empty(),
      stats?.leastFrequentTicket ?? Ticket.empty(),
    );
  }

  //   static fromLotteryInfo(info: xLotteryInfo): LotteryStatistics {
  //     const stats = LotteryStatistics.parseNumberStatistics(
  //       Number(info.lotteryConfig?.pools?.mainNumbers ?? 0n),
  //       Number(info.lotteryConfig?.pools?.luckyStars ?? 0n),
  //       Number(info.lotteryConfig?.ticketStructure?.mainNumbers ?? 0n),
  //       Number(info.lotteryConfig?.ticketStructure?.luckyStars ?? 0n),
  //       info.mainNumbersStatistics,
  //       info.luckyStarsStatistics,
  //     );
  //     return new LotteryStatistics(
  //       new USDC(info.lotteryStatistics?.prizesAwarded ?? 0n),
  //       new USDC(info.lotteryStatistics?.raised?.donations ?? 0n),
  //       stats?.numberFrequencyMap ?? new Map<number, number>(),
  //       stats?.starFrequencyMap ?? new Map<number, number>(),
  //       stats?.numberLastSeenMap ?? new Map<number, number>(),
  //       stats?.starLastSeenMap ?? new Map<number, number>(),
  //       stats?.overdueTicket ?? Ticket.empty(),
  //       stats?.hottestTicket ?? Ticket.empty(),
  //       stats?.mostFrequentTicket ?? Ticket.empty(),
  //       stats?.leastFrequentTicket ?? Ticket.empty(),
  //     );
  //   }

  //   updateWithStatisticsUpdatedEvent(event: xStatisticsUpdatedEvent, lotteryConfig?: LotteryConfig): LotteryStatistics {
  //     if (lotteryConfig == undefined) {
  //       console.log("LotteryConfig is undefined in updateWithStatisticsUpdatedEvent.");
  //       return this;
  //     }
  //     const stats = LotteryStatistics.parseNumberStatistics(
  //       Number(lotteryConfig.numberPool ?? 0n),
  //       Number(lotteryConfig.starPool ?? 0n),
  //       Number(lotteryConfig.numbersPerTicket ?? 0n),
  //       Number(lotteryConfig.starsPerTicket ?? 0n),
  //       event.mainNumbersStatistics,
  //       event.luckyStarsStatistics,
  //     );
  //     this.totalAwarded = new EthereumValue(
  //       event.lotteryStatistics?.prizesAwarded ?? 0n,
  //     );
  //     this.totalRaised = new EthereumValue(
  //       event.lotteryStatistics?.raised?.donations ?? 0n,
  //     );
  //     this.numberFrequency =
  //       stats?.numberFrequencyMap ?? new Map<number, number>();
  //     this.starFrequency = stats?.starFrequencyMap ?? new Map<number, number>();
  //     this.numberLastSeen = stats?.numberLastSeenMap ?? new Map<number, number>();
  //     this.starLastSeen = stats?.starLastSeenMap ?? new Map<number, number>();
  //     this.mostOverdueTicket = stats?.overdueTicket ?? Ticket.empty();
  //     this.hottestTicket = stats?.hottestTicket ?? Ticket.empty();
  //     this.mostFrequentTicket = stats?.mostFrequentTicket ?? Ticket.empty();
  //     this.leastFrequentTicket = stats?.leastFrequentTicket ?? Ticket.empty();
  //     return this;
  //   }

  /**
   * @description A utility function to parse details from a __{@link xNumbersStatistics}__ array.
   **/
  static parseDetailsFromNumberStatistics(
    stats: Frequency[],
    span: number,
    count: number,
  ) {
    const frequencyMap = new Map<number, number>();
    const lastSeenMap = new Map<number, number>();
    const mostFrequent: [number, number][] = [];
    const leastFrequent: [number, number][] = [];
    const mostOverdue: [number, number][] = [];
    const hottest: [number, number][] = [];

    for (let i: number = 0; i < span; i++) {
      const number: number = i + 1;
      const frequency: number = Number(stats[i].frequency);
      const lastDrawn: number = Number(stats[i].lastClaim);
      // Update maps:
      frequencyMap.set(number, frequency);
      lastSeenMap.set(number, lastDrawn);
      // Update most frequent:
      if (mostFrequent.length < count) {
        mostFrequent.push([frequency, number]);
      } else if (frequency > mostFrequent[count - 1][0]) {
        mostFrequent[count - 1] = [frequency, number];
      }
      mostFrequent.sort((a, b) => b[0] - a[0]);
      // Update least frequent:
      if (leastFrequent.length < count) {
        leastFrequent.push([frequency, number]);
      } else if (frequency < leastFrequent[count - 1][0]) {
        leastFrequent[count - 1] = [frequency, number];
      }
      leastFrequent.sort((a, b) => a[0] - b[0]);
      // Update most overdue:
      if (mostOverdue.length < count) {
        mostOverdue.push([lastDrawn, number]);
      } else if (lastDrawn < mostOverdue[count - 1][0]) {
        mostOverdue[count - 1] = [lastDrawn, number];
      }
      mostOverdue.sort((a, b) => a[0] - b[0]);
      // Update hottest:
      if (hottest.length < count) {
        hottest.push([lastDrawn, number]);
      } else if (lastDrawn > hottest[count - 1][0]) {
        hottest[count - 1] = [lastDrawn, number];
      }
      hottest.sort((a, b) => b[0] - a[0]);
    }
    return {
      frequencyMap,
      lastSeenMap,
      mostFrequent: mostFrequent.map((pair) => pair[1]),
      leastFrequent: leastFrequent.map((pair) => pair[1]),
      mostOverdue: mostOverdue.map((pair) => pair[1]),
      hottest: hottest.map((pair) => pair[1]),
    };
  }

  /**
   * @description Converts a __{@link xNumbersStatistics}__ object to 2 maps.
   * One containing the number frequency and another containing the star frequency.
   * Also returns 4 __{@link Ticket}__ objects. One for the most overdue ticket,
   * one for the hottest ticket, one for the most frequent ticket and one for the
   * least frequent ticket.
   * @param numberStatistics The __{@link xNumbersStatistics}__ object to parse.
   * @returns An object containing all outputs.
   */
  static parseNumberStatistics(
    currentNumberPool: number,
    currentStarPool: number,
    currentNumbersPerTicket: number,
    currentStarsPerTicket: number,
    mainNumbers?: Frequency[],
    luckyStars?: Frequency[],
  ) {
    if (mainNumbers == undefined || luckyStars == undefined) {
      console.log("Number statistics are undefined.");
      return undefined;
    }
    const {
      frequencyMap: numberFrequencyMap,
      lastSeenMap: numberLastSeenMap,
      mostFrequent: mostFrequentNumbers,
      leastFrequent: leastFrequentNumbers,
      mostOverdue: mostOverdueNumbers,
      hottest: hottestNumbers,
    } = this.parseDetailsFromNumberStatistics(
      mainNumbers,
      currentNumberPool,
      Number(currentNumbersPerTicket),
    );
    const {
      frequencyMap: starFrequencyMap,
      lastSeenMap: starLastSeenMap,
      mostFrequent: mostFrequentStars,
      leastFrequent: leastFrequentStars,
      mostOverdue: mostOverdueStars,
      hottest: hottestStars,
    } = this.parseDetailsFromNumberStatistics(
      luckyStars,
      currentStarPool,
      Number(currentStarsPerTicket),
    );
    return {
      numberFrequencyMap,
      starFrequencyMap,
      numberLastSeenMap,
      starLastSeenMap,
      overdueTicket: new Ticket(
        mostOverdueNumbers.map((n) => BigInt(n)),
        mostOverdueStars.map((n) => BigInt(n)),
      ),
      hottestTicket: new Ticket(
        hottestNumbers.map((n) => BigInt(n)),
        hottestStars.map((n) => BigInt(n)),
      ),
      mostFrequentTicket: new Ticket(
        mostFrequentNumbers.map((n) => BigInt(n)),
        mostFrequentStars.map((n) => BigInt(n)),
      ),
      leastFrequentTicket: new Ticket(
        leastFrequentNumbers.map((n) => BigInt(n)),
        leastFrequentStars.map((n) => BigInt(n)),
      ),
    };
  }
}
